import type { QueryClient } from '@tanstack/react-query';
import { SherpaProjectReactQueryKey, TdmReactQueryKey } from '@/constants/ReactQueryKeys';
import type { QueryParamsInterface } from '@/types/Shared';
import type { TdmType } from '@/types/Tdm';

const tdmListKeys: Record<TdmType, TdmReactQueryKey> = {
  task: TdmReactQueryKey.GET_PROJECT_TASKS,
  deliverable: TdmReactQueryKey.GET_PROJECT_DELIVERABLES,
  milestone: TdmReactQueryKey.GET_PROJECT_MILESTONES,
};

const tdmDetailKeys: Record<TdmType, string> = {
  task: 'task',
  deliverable: 'deliverable',
  milestone: 'milestone',
};

function projectIdFromParams(params?: QueryParamsInterface): number | null {
  const value = params?.project_id;
  if (typeof value === 'number') return value;
  if (typeof value === 'string' && value.trim() !== '') {
    const projectId = Number(value);
    return Number.isFinite(projectId) ? projectId : null;
  }
  return null;
}

export const projectQueryKey = (projectId: number, params?: QueryParamsInterface) =>
  [SherpaProjectReactQueryKey.GET_PROJECT, projectId, params] as const;

export const projectTeamQueryKey = (projectId: number, params?: QueryParamsInterface) =>
  [SherpaProjectReactQueryKey.GET_PROJECT_MEMBERS, projectId, params] as const;

export const sherpaTdmListQueryKey = (type: TdmType, params?: QueryParamsInterface) =>
  [tdmListKeys[type], projectIdFromParams(params), params] as const;

export const sherpaTdmDetailQueryKey = (type: TdmType, tdmId: number) => [tdmDetailKeys[type], tdmId] as const;

async function invalidate(queryClient: QueryClient, queryKey: readonly unknown[]) {
  await queryClient.invalidateQueries({ queryKey });
}

export async function refreshSherpaTdmQueries(queryClient: QueryClient, type?: TdmType, tdmId?: number, projectId?: number) {
  const types = type ? [type] : (Object.keys(tdmListKeys) as TdmType[]);

  await Promise.all([
    ...types.flatMap((currentType) => [
      invalidate(queryClient, projectId ? [tdmListKeys[currentType], projectId] : [tdmListKeys[currentType]]),
      invalidate(queryClient, tdmId && type === currentType ? sherpaTdmDetailQueryKey(currentType, tdmId) : [tdmDetailKeys[currentType]]),
    ]),
    invalidate(queryClient, ['tdm-analytics']),
  ]);
}

export async function refreshProjectTeamQueries(queryClient: QueryClient, projectId: number) {
  await Promise.all([
    invalidate(queryClient, [SherpaProjectReactQueryKey.GET_PROJECT, projectId]),
    invalidate(queryClient, [SherpaProjectReactQueryKey.GET_PROJECT_MEMBERS, projectId]),
    refreshSherpaTdmQueries(queryClient, undefined, undefined, projectId),
  ]);
}

export async function refreshTemplateProjectQueries(queryClient: QueryClient, projectId: number) {
  await refreshProjectTeamQueries(queryClient, projectId);
}

export async function refreshProjectsListQueries(queryClient: QueryClient) {
  await invalidate(queryClient, ['get-projects']);
}
